import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import User from "@mapmycustomers/shared/types/User";

import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import useView from "@app/component/view/utils/useView";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./QuickOwnerFilter.module.scss";

interface Props {
  users: User[];
}

const QuickOwnerFilter: React.FC<Props> = ({ users }) => {
  const intl = useIntl();
  const analyticsIssuer = useAnalytics();

  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Deal>();

  const handleChange = useCallback(
    (userIds: Array<User["id"]>) => {
      updateFilterModel({
        user: userIds.length ? { operator: FilterOperator.IN_ANY, value: userIds } : undefined,
      });
      analyticsIssuer.clicked(["Select User"]);
    },
    [analyticsIssuer, updateFilterModel]
  );

  const userIds = useMemo(
    () =>
      filter.user &&
      isSimpleCondition(filter.user) &&
      filter.user.operator === FilterOperator.IN_ANY
        ? (filter.user.value as Array<User["id"]>)
        : [],
    [filter]
  );

  return (
    <UserMultiSelectField
      className={styles.container}
      onChange={handleChange}
      oneLiner
      placeholder={intl.formatMessage({
        id: "deals.toolbar.ownerFilter.placeholder",
        defaultMessage: "All Users",
        description:
          "Placeholder displayed in user filter on QuickOwnerFilter component for Deals View",
      })}
      size="middle"
      users={users}
      value={userIds}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(QuickOwnerFilter);
